import {
  Component,
  Input,
  OnDestroy,
  OnInit,
  OnChanges,
  SimpleChanges,
  ViewChild,
  ElementRef,
  AfterViewInit,
} from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { appConfig } from 'app/core/config/app.config';
import { Subject, Subscription, takeUntil } from 'rxjs';

@Component({
  selector: 'text-area',
  template: `<div class="w-full mb-4 form-group">
    <label *ngIf="label !== ''" [for]="controlId" [class]="lableStyle">
      {{ label }}
      <span
        *ngIf="isRequired() && label !== '' && !removeRequired"
        class="text-red-500"
      >
        *
      </span>
      <span class="mt-2" *ngIf="tooltipText">
        <mat-icon
          svgIcon="mat_outline:info"
          [matTooltip]="tooltipText"
          [matTooltipPosition]="tooltipPosition"
          [matTooltipShowDelay]="showDelay"
          [matTooltipHideDelay]="hideDelay"
          class="text-grey-500 cursor-pointer icon-size-5 relative top-[2px]"
        ></mat-icon>
      </span>
    </label>
    <textarea
      #textarea
      [placeholder]="placeholder"
      [formControl]="controlName"
      autocomplete="off"
      [id]="controlId"
      [name]="controlId"
      class="mt-1 w-full border border-gray-300 py-2 px-4 rounded-md form-control"
      [ngClass]="[controlName.disabled ? disabledBG || 'bg-gray-100' : '']"
      [class.is-invalid]="controlName.invalid && controlName.touched"
    ></textarea>
    <span
      [style.visibility]="
        !!controlName && controlName.enabled ? 'visible' : 'hidden'
      "
      class="text-sm text-gray-700 ml-2"
    >
      Character count: {{ textCount ? textCount : '0' }} / {{ textLimit }}
    </span>
    <form-validator [controlName]="controlName"></form-validator>
    <!--  <div *ngIf="exceedsMaxLength" class="text-sm text-red-500 mt-1 ml-2">
      This field exceeds the character limit of {{ textLimit }}.
    </div> -->
  </div>`,
})
export class TextAreaComponent
  implements OnInit, OnDestroy, OnChanges, AfterViewInit
{
  @Input() label: string = '';
  @Input() controlName: FormControl = new FormControl('');
  @Input() placeholder: string = '';
  @Input() setLimit: number = 0;
  @Input() lableStyle: string = 'block text-sm font-medium text-gray-700';
  @Input() disabled: boolean = false;
  @Input() removeRequired: boolean = false;
  @Input() disabledBG: string = 'bg-gray-100';

  @Input() tooltipText: string = '';
  @Input() tooltipPosition: 'above' | 'below' | 'left' | 'right' = 'above';
  @Input() showDelay: number = 200;
  @Input() hideDelay: number = 100;

  @ViewChild('textarea') textareaRef!: ElementRef<HTMLTextAreaElement>;

  protected ngUnsubscribe: Subject<void> = new Subject<void>();
  private controlValueChangesSub: Subscription | null = null;

  public textCount = 0;
  public textLimit = 300;
  public exceedsMaxLength = false;
  public controlId: string = '';
  ngOnInit(): void {
    this.textLimit =
      this.setLimit > 0 ? this.setLimit : (appConfig?.textAreaLimit ?? 300);

    this.textCount = this.controlName?.value?.length ?? 0;

    this.controlId = this.generateIdFromLabel(this.label);

    this.syncDisabledState(this.disabled);
    this.bindControl();
  }

  ngAfterViewInit(): void {
    setTimeout(() => this.autoResize());
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ('disabled' in changes && this.controlName) {
      this.syncDisabledState(changes['disabled']?.currentValue);
    }
    if ('controlName' in changes && this.controlName) {
      if (!changes['controlName'].firstChange) {
        if (this.controlValueChangesSub) {
          this.controlValueChangesSub.unsubscribe();
          this.controlValueChangesSub = null;
        }
        this.syncDisabledState(this.disabled);
        this.bindControl();
      }
    }
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
    this.controlValueChangesSub?.unsubscribe();
  }

  // =========================
  // CORE LOGIC
  // =========================

  autoResize(): void {
    if (!this.textareaRef) return;

    const textarea = this.textareaRef.nativeElement;
    textarea.style.height = 'auto';
    textarea.style.height = textarea.scrollHeight + 'px';
  }

  private bindControl(): void {
    if (!this.controlName) return;

    this.addLimitValidation();
    this.textCount = this.controlName.value?.length ?? 0;

    this.controlValueChangesSub = this.controlName.valueChanges
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((v) => {
        this.textCount = v?.length ?? 0;
        this.addLimitValidation();
        if (this.disabled) {
          this.autoResize();
        }
      });

    setTimeout(() => this.autoResize());
  }

  private syncDisabledState(disabled: boolean) {
    if (!this.controlName) return;
    if (disabled && !this.controlName.disabled) {
      this.controlName.disable({ emitEvent: false });
    } else if (!disabled && this.controlName.disabled) {
      this.controlName.enable({ emitEvent: false });
    }
  }

  private addLimitValidation() {
    this.exceedsMaxLength = this.textCount > this.textLimit;

    this.controlName.addValidators([Validators.maxLength(this.textLimit)]);
    this.controlName.updateValueAndValidity({ emitEvent: false });
  }

  public isRequired(): boolean {
    // check form if they have a required validation
    return this.controlName.hasValidator(Validators.required);
  }

  /** Generate ID from label with random number */
  private generateIdFromLabel(label: string): string {
    const slug = label.toLowerCase().replace(/\s+/g, '-');
    const random = Math.floor(Math.random() * 10000); // 0-9999
    return `${slug}-${random}`;
  }
}
